import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import PricingGrid from '@/components/PricingGrid';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ShieldCheck, CreditCard, ArrowLeft, Sparkles } from 'lucide-react';

const PLAN_LABEL = { free: 'No subscription', try: 'Try', starter: 'Starter', growth: 'Growth', enterprise: 'Enterprise' };

export default function UpgradePlanPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [plansRes, billingRes] = await Promise.all([
          api.get('/billing/plans'),
          api.get('/billing/me').catch(() => ({ data: null })),
        ]);
        setPlans(plansRes.data.plans || []);
        setBilling(billingRes.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const currentPlanKey = billing?.plan || 'free';

  return (
    <div className="max-w-[1240px] space-y-6" data-testid="upgrade-plan-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <button
            onClick={() => navigate('/app/settings')}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-2"
            data-testid="upgrade-back-to-settings-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Settings
          </button>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Upgrade your plan</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Choose a plan that fits — you stay signed in to your account the whole way through.
          </p>
        </div>
        <Badge variant="outline" className="text-xs shrink-0" data-testid="upgrade-current-plan-badge">
          Current: {PLAN_LABEL[currentPlanKey] || currentPlanKey}
        </Badge>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-96 rounded-[var(--radius)] bg-secondary animate-pulse" />)}
        </div>
      ) : (
        <PricingGrid plans={plans} currentPlanKey={currentPlanKey} />
      )}

      <Card className="border p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[hsl(var(--accent)/0.12)] text-[hsl(var(--accent))] shrink-0">
            <ShieldCheck className="h-4.5 w-4.5" />
          </div>
          <div>
            <div className="text-sm font-medium">Secure billing via Dodo Payments</div>
            <div className="text-xs text-muted-foreground mt-0.5">Every paid plan starts with a 7-day free trial. We never store your card details. Cancel anytime from Settings.</div>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate('/app/settings')} data-testid="upgrade-manage-billing-link">
          <CreditCard className="h-3.5 w-3.5 mr-1.5" /> Manage billing in Settings
        </Button>
      </Card>
    </div>
  );
}
